import {getAudioSettings,subscribeAudioSettings,type SoundCategory} from "./audio-settings";

type Outputs={sfx:GainNode;music:GainNode;dispose:()=>void;guard?:WaveShaperNode;guardUsers:number};
const contexts=new WeakMap<AudioContext,Outputs>();
/** Every audible source terminates in one category bus, never directly in destination.
 * Bus gain updates mute currently playing voices as well as future sounds.
 */
export function getSoundOutput(context:AudioContext,category:SoundCategory="sfx"):GainNode{
  let outputs=contexts.get(context);
  if(!outputs){
    const sfx=context.createGain(),music=context.createGain();
    const update=()=>{
      if(context.state==="closed")return;
      const settings=getAudioSettings();
      sfx.gain.setValueAtTime(settings.sfx?1:0,context.currentTime);
      music.gain.setValueAtTime(settings.music?1:0,context.currentTime);
    };
    update();sfx.connect(context.destination);music.connect(context.destination);
    const unsubscribe=subscribeAudioSettings(update);
    const closed=()=>{if(context.state==="closed")disposeSoundOutputs(context);};
    outputs={sfx,music,guardUsers:0,dispose:()=>{
      unsubscribe();sfx.disconnect();music.disconnect();
      outputs?.guard?.disconnect();
      context.removeEventListener?.("statechange",closed);
    }};
    contexts.set(context,outputs);context.addEventListener?.("statechange",closed);
  }
  return outputs[category];
}
/** Optional scene-scoped summed-output protection. Unity below .8; smoothly
 * rounds only hot transients, with a ceiling below full scale. No auto makeup
 * gain or pumping. Scenes not opting in retain their original audio graph.
 */
export function soundPeakGuardCurve():Float32Array<ArrayBuffer>{
  const curve=new Float32Array(4097);
  for(let i=0;i<curve.length;i++){
    const x=i/(curve.length-1)*2-1,a=Math.abs(x);
    curve[i]=a<=.8?x:Math.sign(x)*(.8+.18*Math.tanh((a-.8)/.18));
  }
  return curve;
}
export function acquireSoundPeakGuard(context:AudioContext):()=>void{
  getSoundOutput(context);
  const outputs=contexts.get(context)!;
  if(!outputs.guard){
    const guard=context.createWaveShaper();
    guard.curve=soundPeakGuardCurve();guard.oversample="4x";
    guard.connect(context.destination);
    outputs.sfx.disconnect();outputs.music.disconnect();
    outputs.sfx.connect(guard);outputs.music.connect(guard);outputs.guard=guard;
  }
  outputs.guardUsers++;
  let released=false;
  return ()=>{
    if(released)return;released=true;
    if(contexts.get(context)!==outputs||--outputs.guardUsers>0)return;
    outputs.sfx.disconnect();outputs.music.disconnect();outputs.guard?.disconnect();
    outputs.guard=undefined;
    if(context.state!=="closed"){
      outputs.sfx.connect(context.destination);outputs.music.connect(context.destination);
    }
  };
}
export function disposeSoundOutputs(context:AudioContext){
  const outputs=contexts.get(context);contexts.delete(context);outputs?.dispose();
}
